import { Flex, Spinner, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import "./style.css";
import { useTranslations } from "next-intl";
import RadialLoading from "../Loading/RadialLoading";
import { KnootLottie } from "../lotties/Lottie";

interface DynamicLoadingProps {
  isLoading: boolean;
  loadingTexts?: string[];
  colorPalette?: string;
  intervalNumber?: number;
  showLoadingIcon?: boolean;
  fontSize?: string;
  textColor?: string;
  loadingType?: "spinner" | "lottie" | "radial";
}

export const DynamicLoading = ({
  isLoading,
  loadingTexts,
  loadingType = "lottie",
  colorPalette = "primary",
  intervalNumber = 5500,
  showLoadingIcon = true,
  fontSize = "md",
  textColor = "neutral.700",
}: DynamicLoadingProps) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const t = useTranslations(
    "resume_community.talent_pool.search_filters.loading",
  );
  loadingTexts = useMemo(() => {
    if (!loadingTexts || loadingTexts.length === 0) {
      return [
        t("loading_your_content"),
        t("aggregating_data"),
        t("collecting_information"),
        t("analyzing_data"),
        t("working_with_LLM"),
        t("aggregating_data_with_LLM"),
        t("almost_done"),
      ];
    }
    return loadingTexts;
  }, [t, loadingTexts]);
  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % loadingTexts.length);
    }, intervalNumber);

    return () => clearInterval(interval);
  }, [intervalNumber, isLoading, loadingTexts.length]);

  const loadingComponent = useMemo(() => {
    if (!showLoadingIcon) {
      return null;
    }
    if (loadingType === "lottie") {
      return (
        <KnootLottie animationName="fileLoading" height="320px" width="320px" />
      );
    }

    if (loadingType === "radial") {
      return <RadialLoading />;
    }

    return (
      <Spinner
        size="md"
        color={`${colorPalette}.500`}
        colorPalette={colorPalette}
      />
    );
  }, [
    loadingType,
    showLoadingIcon,
    colorPalette,
    loadingTexts,
    currentTextIndex,
  ]);

  if (!isLoading) return null;

  return (
    <Flex align="center" gap="1rem" flexDirection="column">
      {loadingComponent}
      {loadingTexts.map((text, index) => (
        <Text
          key={index}
          color={textColor}
          mt="-3rem"
          data-state={index === currentTextIndex ? "open" : "closed"}
          hidden={index !== currentTextIndex}
          _open={{
            animation: "slideInFromLeft 600ms ease-out",
          }}
          _closed={{
            animation: "slideInFromRight 600ms ease-out",
          }}
          textStyle={fontSize}
          fontWeight="normal"
        >
          {text}
        </Text>
      ))}
    </Flex>
  );
};
